import { useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { EChart } from "@/components/charts/EChart";
import { ReportBreadcrumbs } from "@/components/report/ReportBreadcrumbs";
import { ReportContextSummary } from "@/components/report/ReportContextSummary";
import { ReportExportPanel } from "@/components/report/ReportExportPanel";
import { FloatingReportLayout } from "@/components/report/FloatingReportLayout";
import { ReportSourceError } from "@/components/report/ReportSourceError";
import { useReportAnalysisPoints } from "@/hooks/useReportAnalysis";
import { useStudentAssessmentsQuery, useStudentQuery } from "@/hooks/useStudents";
import { useCatalog } from "@/providers/CatalogProvider";
import type { Assessment } from "@/types/assessment";
import { formatDate } from "@/utils/date";
import {
  formatPercent,
  formatScore,
  areAssessmentsComparable,
  buildAnalyticsPresentation,
  renderAssessmentStatus,
  renderAssessmentType,
  resolveComparableAssessmentIds,
  type AssessmentAnalysisPoint,
  type ReportDomainStat,
} from "@/utils/report-analysis";
import { completedAssessmentsForStudent } from "@/utils/student-workspace";

const ANALYTICS_TOC_ITEMS = [
  { id: "analytics-overview", label: "关键指标" },
  { id: "analytics-trend", label: "总体趋势" },
  { id: "analytics-domain-charts", label: "领域图表" },
  { id: "analytics-domain-details", label: "领域明细" },
  { id: "analytics-guidance", label: "变化与建议" },
];

export function ReportsAnalyticsPage(): JSX.Element {
  const { studentId = "" } = useParams<{ studentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentQuery = useStudentQuery(studentId);
  const assessmentsQuery = useStudentAssessmentsQuery(studentId);
  const completedAssessments = completedAssessmentsForStudent(
    assessmentsQuery.data ?? [],
    studentId,
  );
  const requestedAssessmentId = searchParams.get("assessmentId");
  const selectedSource = requestedAssessmentId
    ? completedAssessments.find((assessment) => assessment.id === requestedAssessmentId)
    : completedAssessments[0];
  const assessmentOptions = completedAssessments.filter(
    (assessment) => assessment.integrityStatus === "verified",
  );
  const selectedAssessmentIds = resolveSelectedAssessmentIds(
    searchParams.get("assessments") ?? selectedSource?.id ?? null,
    assessmentOptions,
  );
  const selectedAssessments = assessmentOptions.filter((assessment) =>
    selectedAssessmentIds.includes(assessment.id),
  );
  const comparisonReference = selectedAssessments[0];
  const { scaleLibrary } = useCatalog();
  const analysis = useReportAnalysisPoints(selectedAssessments, scaleLibrary);
  const points = analysis.points;
  const firstPoint = points[0];
  const latestPoint = points[points.length - 1];
  const presentation = useMemo(() => buildAnalyticsPresentation(points), [points]);
  const totalGrowth = presentation.totalGrowth ?? 0;
  const averageGrowth = presentation.averageGrowth ?? 0;
  const latestStrengths = useMemo(
    () => buildDomainHighlights(latestPoint?.domainStats ?? [], "strength"),
    [latestPoint],
  );
  const focusDomains = useMemo(
    () => buildDomainHighlights(latestPoint?.domainStats ?? [], "focus"),
    [latestPoint],
  );
  const trendSegments = useMemo(() => buildTrendSegments(points), [points]);

  const totalTrendOption = useMemo(() => {
    if (!presentation.showTotalTrend) return undefined;
    const maxScore = Math.max(...points.map((item) => item.totalScore), 10);
    return {
      title: { text: "总分趋势", left: "center", textStyle: { fontSize: 16 } },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: points.map((item) => formatDate(item.assessment.startedAt)),
      },
      yAxis: { type: "value", min: 0, max: Math.ceil(maxScore * 1.2) },
      series: [
        {
          type: "line",
          data: points.map((item) => Number(item.totalScore.toFixed(2))),
          smooth: true,
          areaStyle: { opacity: 0.1 },
        },
      ],
    };
  }, [points, presentation.showTotalTrend]);

  const domainRadarOption = useMemo(() => {
    if (!latestPoint) {
      return undefined;
    }
    return {
      title: { text: "最新领域平均分", left: "center", textStyle: { fontSize: 16 } },
      tooltip: {},
      radar: {
        indicator: latestPoint.domainStats.map((domain) => ({
          name: domain.name.replace("领域", ""),
          max: 2,
        })),
        radius: "65%",
      },
      series: [
        {
          name: "平均分",
          type: "radar",
          areaStyle: { opacity: 0.15 },
          data: [
            {
              value: latestPoint.domainStats.map((domain) =>
                Number((domain.average ?? 0).toFixed(2)),
              ),
            },
          ],
        },
      ],
    };
  }, [latestPoint]);

  const domainBarOption = useMemo(() => {
    if (!latestPoint) {
      return undefined;
    }
    return {
      title: { text: "最新领域平均分", left: "center", textStyle: { fontSize: 16 } },
      tooltip: { trigger: "axis" },
      xAxis: {
        type: "category",
        data: presentation.domainAverageSeries.map((domain) => domain.name),
      },
      yAxis: { type: "value", min: 0, max: 2 },
      series: [
        {
          type: "bar",
          data: presentation.domainAverageSeries.map((domain) => domain.value),
          itemStyle: { color: "#527a2a" },
        },
      ],
    };
  }, [latestPoint, presentation.domainAverageSeries]);

  function setAssessmentSelection(ids: string[]): void {
    const next = new URLSearchParams(searchParams);
    if (ids.length > 0) {
      next.set("assessments", ids.join(","));
      next.set("assessmentId", ids[0]);
    } else {
      next.delete("assessments");
    }
    next.set("select", "1");
    setSearchParams(next);
  }

  function toggleAssessment(id: string): void {
    const exists = selectedAssessmentIds.includes(id);
    const assessment = assessmentOptions.find((item) => item.id === id);
    if (
      !exists &&
      (!assessment ||
        (comparisonReference && !areAssessmentsComparable(comparisonReference, assessment)))
    ) {
      return;
    }
    setAssessmentSelection(
      exists ? selectedAssessmentIds.filter((item) => item !== id) : [...selectedAssessmentIds, id],
    );
  }

  const isViewing = searchParams.get("select") !== "1" && selectedAssessmentIds.length > 0;
  const selectionParams = new URLSearchParams();
  if (selectedAssessmentIds.length > 0) {
    selectionParams.set("assessments", selectedAssessmentIds.join(","));
    selectionParams.set("assessmentId", selectedAssessmentIds[0]);
  }

  function openReport(): void {
    const next = new URLSearchParams(selectionParams);
    next.delete("select");
    setSearchParams(next);
  }

  if (requestedAssessmentId && !selectedSource) {
    return (
      <ReportSourceError
        studentId={studentId}
        title="评估记录不属于当前儿童"
        description="请返回当前儿童的报告页重新选择评估记录。"
      />
    );
  }

  if (
    selectedSource &&
    selectedSource.integrityStatus !== "verified" &&
    !searchParams.has("assessments")
  ) {
    return (
      <ReportSourceError
        studentId={studentId}
        title="这份评估暂不能生成正式报告"
        description="评估尚未通过完整性校验，系统不会自动改用另一份历史记录。"
      />
    );
  }

  if (!isViewing) {
    return (
      <div className="space-y-6">
        <header>
          <ReportBreadcrumbs currentPage="统计分析" studentId={studentId} />
          <h1 className="text-2xl font-semibold text-slate-800">选择统计范围</h1>
          <p className="mt-2 text-sm text-slate-500">
            选择当前儿童一份或多份同口径评估记录，再进入固定的统计报告。
          </p>
        </header>

        <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div>
            <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="mt-1 text-base font-semibold text-slate-800">选择评估记录</h2>
              </div>
              <p className="text-sm text-slate-500">已选 {selectedAssessmentIds.length} 份</p>
            </div>

            {assessmentsQuery.isLoading ? (
              <p className="mt-3 text-sm text-slate-500">正在加载评估记录…</p>
            ) : null}
            {assessmentsQuery.isError ? (
              <p className="mt-3 text-sm text-red-500">评估记录加载失败，请稍后重试。</p>
            ) : null}

            <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {assessmentOptions.map((assessment) => (
                <AssessmentChoice
                  key={assessment.id}
                  assessment={assessment}
                  checked={selectedAssessmentIds.includes(assessment.id)}
                  disabled={
                    !selectedAssessmentIds.includes(assessment.id) &&
                    comparisonReference !== undefined &&
                    !areAssessmentsComparable(comparisonReference, assessment)
                  }
                  onChange={() => toggleAssessment(assessment.id)}
                />
              ))}
            </div>

            {assessmentOptions.length === 0 && !assessmentsQuery.isLoading ? (
              <p className="mt-3 text-sm text-slate-500">该儿童暂无可用评估记录。</p>
            ) : null}
          </div>

          <div className="mt-6 flex justify-end border-t border-slate-200 pt-5">
            <button
              type="button"
              onClick={openReport}
              disabled={selectedAssessmentIds.length === 0}
              className="min-h-11 rounded-md bg-brand px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              查看统计报告
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <ReportBreadcrumbs currentPage="统计分析" studentId={studentId} />
          <h1 className="text-2xl font-semibold text-slate-800">评估结果统计与报告</h1>
          <p className="mt-2 text-sm text-slate-500">查看当前水平、趋势与领域表现。</p>
        </div>
        <ReportExportPanel
          onDownloadPdf={() => window.print()}
          disabled={points.length === 0 || analysis.isLoading}
        />
      </header>

      <ReportContextSummary
        studentName={studentQuery.data?.profile.name ?? "未知儿童"}
        assessmentLabels={selectedAssessments.map(
          (assessment) =>
            `${formatDate(assessment.startedAt)} · ${renderAssessmentType(assessment.type)}`,
        )}
      />

      {analysis.isLoading ? <p className="text-sm text-slate-500">正在汇总评分条目…</p> : null}
      {analysis.isError ? (
        <p className="text-sm text-red-500">
          正式报告加载失败，请确认所选记录属于同一儿童、目录版本与条目集合。
        </p>
      ) : null}

      {points.length === 0 ? (
        <section className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
          请选择至少一份评估记录生成统计分析。
        </section>
      ) : (
        <FloatingReportLayout items={ANALYTICS_TOC_ITEMS}>
          <section
            id="analytics-overview"
            className={`scroll-mt-[11.5rem] grid gap-4 ${presentation.showIntervalChange ? "md:grid-cols-3" : "md:grid-cols-2"}`}
          >
            <AnalyticsCard
              title="最新总分"
              value={`${formatScore(latestPoint?.totalScore ?? 0)} / ${formatScore(
                latestPoint?.totalMaxScore ?? 0,
              )}`}
              remark={`完成条目 ${latestPoint?.totalCompleted ?? 0} / ${
                latestPoint?.totalCatalogEntries ?? 0
              }`}
              trend={formatPercent(latestPoint?.totalCompletionPercent ?? 0)}
            />
            {presentation.showIntervalChange ? (
              <AnalyticsCard
                title="区间变化"
                value={`${totalGrowth >= 0 ? "+" : ""}${formatScore(totalGrowth)} 分`}
                remark={`共 ${points.length} 次评估`}
                trend={`${averageGrowth >= 0 ? "+" : ""}${formatScore(averageGrowth)} / 次`}
              />
            ) : null}
            <AnalyticsCard
              title="最新平均分"
              value={
                latestPoint?.totalAverage === null
                  ? "-"
                  : formatScore(latestPoint?.totalAverage ?? 0)
              }
              remark="按已评分条目计算"
              trend={formatPercent(latestPoint?.totalScorePercent ?? 0)}
            />
          </section>

          {presentation.showTotalTrend ? (
            <section
              id="analytics-trend"
              className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-slate-700">总体趋势</h2>
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-4">
                <EChart option={totalTrendOption} height={280} />
              </div>
              <p className="text-sm text-slate-600">
                从 {formatDate(firstPoint?.assessment.startedAt)} 到{" "}
                {formatDate(latestPoint?.assessment.startedAt)}，实得分变化{" "}
                {totalGrowth >= 0 ? "+" : ""}
                {formatScore(totalGrowth)} 分。
              </p>
            </section>
          ) : (
            <section
              id="analytics-trend"
              className="scroll-mt-[11.5rem] rounded-xl border border-brand/20 bg-brand/5 p-5 text-sm text-slate-700"
            >
              当前只有一次可比评估，暂不计算区间变化。完成同一条目集合的下一次评估后，可在这里查看发展趋势。
            </section>
          )}

          <section
            id="analytics-domain-charts"
            className="scroll-mt-[11.5rem] grid gap-4 lg:grid-cols-2"
          >
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
              <EChart option={domainRadarOption} height={320} />
              <p className="text-sm text-slate-600">
                雷达图按最新评估中已评分条目的平均分计算，满分为 2 分。
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-4">
              <EChart option={domainBarOption} height={320} />
              <p className="text-sm text-slate-600">
                柱状图与雷达图统一按已评分条目的平均分展示，满分为 2 分。
              </p>
            </div>
          </section>

          <section
            id="analytics-domain-details"
            className="scroll-mt-[11.5rem] space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <h2 className="text-lg font-semibold text-slate-700">最新领域明细</h2>
            <DomainStatsTable domains={latestPoint?.domainStats ?? []} />
          </section>

          <section
            id="analytics-guidance"
            className="scroll-mt-[11.5rem] grid gap-4 lg:grid-cols-2"
          >
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-3">
              <h3 className="text-lg font-semibold text-slate-700">阶段变化</h3>
              {trendSegments.length > 0 ? (
                <ul className="space-y-2 text-sm text-slate-600">
                  {trendSegments.map((segment) => (
                    <li key={segment.key}>{segment.description}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-slate-500">选择两次及以上评估后展示阶段变化。</p>
              )}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm space-y-3">
              <h3 className="text-lg font-semibold text-slate-700">综合发展建议</h3>
              <div>
                <p className="font-semibold text-slate-600">保持优势</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                  {latestStrengths.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold text-slate-600">重点关注</p>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
                  {focusDomains.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>
        </FloatingReportLayout>
      )}
    </div>
  );
}

function AssessmentChoice({
  assessment,
  checked,
  disabled,
  onChange,
}: {
  assessment: Assessment;
  checked: boolean;
  disabled: boolean;
  onChange: () => void;
}): JSX.Element {
  const inputId = `analytics-assessment-${assessment.id}`;
  return (
    <label
      htmlFor={inputId}
      className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 text-sm ${
        checked
          ? "border-brand bg-brand/10 text-brand"
          : "border-slate-200 bg-slate-50 text-slate-600"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <input
        id={inputId}
        type="checkbox"
        aria-label={`选择评估记录 ${formatDate(assessment.startedAt)} ${renderAssessmentType(assessment.type)}`}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="mt-1 h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand"
      />
      <span>
        <span className="block font-semibold">
          {formatDate(assessment.startedAt)} · {renderAssessmentType(assessment.type)}
        </span>
        <span className="text-xs text-slate-500">
          {renderAssessmentStatus(assessment.status)} ·{" "}
          {assessment.evaluatorDisplayName ?? "历史评估师"}
        </span>
      </span>
    </label>
  );
}

function AnalyticsCard({
  title,
  value,
  remark,
  trend,
}: {
  title: string;
  value: string;
  remark: string;
  trend: string;
}): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-slate-400">{title}</p>
      <p className="mt-2 text-2xl font-semibold text-slate-800">{value}</p>
      <p className="mt-1 text-sm text-slate-600">{remark}</p>
      <p className="mt-2 text-xs font-semibold text-emerald-600">{trend}</p>
    </div>
  );
}

function DomainStatsTable({ domains }: { domains: ReportDomainStat[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left font-medium">领域</th>
            <th className="px-4 py-2 text-left font-medium">实得分</th>
            <th className="px-4 py-2 text-left font-medium">平均分</th>
            <th className="px-4 py-2 text-left font-medium">完成条目</th>
            <th className="px-4 py-2 text-left font-medium">完成率</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 text-slate-700">
          {domains.map((domain) => (
            <tr key={domain.code}>
              <td className="px-4 py-2 font-semibold">{domain.name}</td>
              <td className="px-4 py-2">
                {formatScore(domain.score)} / {formatScore(domain.maxScore)}
              </td>
              <td className="px-4 py-2">
                {domain.average === null ? "-" : formatScore(domain.average)}
              </td>
              <td className="px-4 py-2">
                {domain.completed} / {domain.totalEntries}
              </td>
              <td className="px-4 py-2">{formatPercent(domain.completionPercent)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function resolveSelectedAssessmentIds(param: string | null, assessments: Assessment[]): string[] {
  if (assessments.length === 0) {
    return [];
  }
  if (param === null) {
    return resolveComparableAssessmentIds(
      assessments.map((assessment) => assessment.id),
      assessments,
    );
  }
  const ids = param
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  return resolveComparableAssessmentIds(ids, assessments);
}

function buildDomainHighlights(domains: ReportDomainStat[], mode: "strength" | "focus"): string[] {
  const candidates = domains.filter((domain) => domain.completed > 0);
  if (candidates.length === 0) {
    return ["暂无已评分条目，完成评估后可生成建议。"];
  }
  const sorted = [...candidates].sort((a, b) =>
    mode === "strength" ? (b.average ?? 0) - (a.average ?? 0) : (a.average ?? 0) - (b.average ?? 0),
  );
  return sorted.slice(0, 2).map((domain) => {
    const average = domain.average === null ? "-" : formatScore(domain.average);
    if (mode === "strength") {
      return `${domain.name}平均分 ${average}，可继续通过日常活动保持稳定表现。`;
    }
    return `${domain.name}平均分 ${average}，建议结合对应短期目标安排后续观察与练习。`;
  });
}

function buildTrendSegments(points: AssessmentAnalysisPoint[]): Array<{
  key: string;
  description: string;
}> {
  return points.slice(1).map((point, index) => {
    const previous = points[index];
    const change = point.totalScore - previous.totalScore;
    return {
      key: `${previous.assessment.id}-${point.assessment.id}`,
      description: `${formatDate(previous.assessment.startedAt)} 至 ${formatDate(
        point.assessment.startedAt,
      )}：实得分 ${change >= 0 ? "+" : ""}${formatScore(change)} 分，完成条目 ${
        point.totalCompleted
      } 项。`,
    };
  });
}
